import {AfterViewInit, Component, effect, ViewChild} from '@angular/core';
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {DialogService} from '../../services/dialog.service';
import {DatePipe, NgClass} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {HttpClient} from '@angular/common/http';
import {CaseDtoModel} from '../../models/DTO/CaseDto.model';
import {UserHal} from '../../models/HAL/user-hal.model';
import {CaseOverviewHal, PagedCaseOverviewHal} from '../../models/HAL/caseOverview-hal.model';
import {MatIcon} from '@angular/material/icon';
import {MatButtonModule, MatIconButton} from '@angular/material/button';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatMenuModule} from '@angular/material/menu';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {DynamicConfirmationDialogComponent} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {CaseStateFilterComponent} from '../../components/case-state-filter/case-state-filter.component';
import {SnackBarService} from '../../services/snackbar.service';
import {AssignCaseComponent} from '../../components/assign-case/assign-case.component';
import {MoveCaseComponent} from '../../components/move-case/move-case.component';
import {Team} from '../../models/HAL/team-hal.model';
import {SessionService} from '../../services/session.service';
import {TenantSpecificAssetPipe} from '../../pipes/tenant-specific-asset.pipe';
import {MatSort, MatSortHeader, SortDirection} from '@angular/material/sort';
import {catchError, debounceTime, finalize, forkJoin, map, of, Subject, switchMap, tap} from 'rxjs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {SelectionActionsBarComponent} from '../../components/selection-actions-bar/selection-actions-bar.component';
import {SelectionActionComponent} from '../../components/selection-action/selection-action.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {buildCasesUrl, CASES_OVERVIEW_STATE_FILTER_OPTIONS, CASES_OVERVIEW_STORAGE_KEY, CasesOverviewPreferences, loadPreferences, stateFilterOptionsWithoutProcessing, stateFilterOptionsWithProcessing} from './cases-overview-preferences';
import {AdditionalUserInfo} from '../../components/smart-text/additional-user-info.component';
import {TableSelectionCellComponent} from '../../components/table-selection-cell/table-selection-cell.component';
import {canAllSelectedCasesBeDownloaded, CaseZipDownloadResult, downloadSelectedCasesDocumentsAsZip, downloadZipBlob, getCaseDownloadFailureSnackbarKey} from './cases-overview-download';

@Component({
  selector: 'app-cases-overview',
  imports: [
    TranslatePipe,
    DatePipe,
    MatButtonModule,
    MatIconButton,
    MatFormFieldModule,
    MatTableModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinner,
    MatSlideToggle,
    MatSort,
    MatSortHeader,
    MatIcon,
    MatInput,
    ReactiveFormsModule,
    NgClass,
    TenantSpecificAssetPipe,
    CaseStateFilterComponent,
    AdditionalUserInfo,
    MatTooltipModule,
    SelectionActionsBarComponent,
    SelectionActionComponent,
    TableSelectionCellComponent
  ],
  templateUrl: './cases-overview.component.html',
  styleUrl: './cases-overview.component.scss'
})
export class CasesOverviewComponent implements AfterViewInit {

  protected pagedCaseOverviewHal: PagedCaseOverviewHal;
  protected loadingCases: boolean = true;
  protected createCaseLink: string | undefined = undefined;
  protected searchCasesControl: FormControl;
  protected readonly displayedColumns: string[] = ['selection', 'name', 'client', 'assignee', 'createdDate', 'lastModifiedDate', 'state', 'action'];
  protected dataSource: MatTableDataSource<CaseOverviewHal> = new MatTableDataSource<CaseOverviewHal>([]);
  protected sortActive: string = 'createdDate';
  protected sortDirection: SortDirection = 'desc';
  protected onlyShowMyCases: boolean = false;
  protected stateFilterOptions: string[] = CASES_OVERVIEW_STATE_FILTER_OPTIONS;
  protected selectedStateFilters: string[] = [...this.stateFilterOptions];
  protected selectedCases: string[] = [];
  private currentTeamId = "";
  private readonly updateCases$ = new Subject<PageEvent>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    activatedRoute: ActivatedRoute,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly dialogService: DialogService,
    private readonly http: HttpClient,
    private readonly snackBarService: SnackBarService,
    private readonly translateService: TranslateService,
    private readonly sessionService: SessionService,
    public router: Router) {

    // Initialize components
    this.pagedCaseOverviewHal = new PagedCaseOverviewHal(activatedRoute.snapshot.data['cases']);
    this.dataSource = new MatTableDataSource<CaseOverviewHal>(this.pagedCaseOverviewHal._embedded?.caseOverviews);
    this.searchCasesControl = new FormControl();
    // The tenant decides whether the processing phase exists, so the option list has to be known before the stored
    // preferences are applied — they are filtered against it.
    this.stateFilterOptions = this.pagedCaseOverviewHal.processingPhaseEnabled
      ? stateFilterOptionsWithProcessing()
      : stateFilterOptionsWithoutProcessing();
    this.selectedStateFilters = [...this.stateFilterOptions];

    this.applyStoredPreferences();

    this.loadingSpinnerService.hide();
    this.loadingCases = false;

    // React to session changes
    effect(() => {
      this.currentTeamId = this.sessionService.session()?.currentTeam?.identification ?? "";
      if (this.sessionService.session()?.currentTeam?._links.hasLink("createCase")) {
        this.createCaseLink = this.sessionService.session()?.currentTeam?._links?.getLink("createCase")?.getRoute();
      }
    });

    // React to search input changes
    this.searchCasesControl.valueChanges.pipe(
      debounceTime(250)
    ).subscribe(() => {
      this.persistPreferences();
      this.reloadCasesWithNewParams();
    });

    // Use switchMap to cancel previous in-flight requests when a new one is triggered
    this.updateCases$.pipe(
      takeUntilDestroyed(),
      switchMap(pageEvent => {
        this.loadingCases = true;
        const url = this.buildCasesUrl(pageEvent);
        return this.http.get<PagedCaseOverviewHal>(url.href).pipe(
          catchError(() => {
            this.loadingCases = false;
            this.snackBarService.open('casesOverview.snackbars.loadError', 3, 'error');
            return of(null);
          })
        );
      })
    ).subscribe(pagedCaseOverviewHal => {
      if (!pagedCaseOverviewHal) {
        return;
      }
      this.pagedCaseOverviewHal = new PagedCaseOverviewHal(pagedCaseOverviewHal);
      this.dataSource = new MatTableDataSource<CaseOverviewHal>(this.pagedCaseOverviewHal._embedded?.caseOverviews);
      const selectableCaseIds = new Set(this.getSelectableCases().map(caseOverview => caseOverview.identification));
      this.selectedCases = this.selectedCases.filter(selectedCaseId => selectableCaseIds.has(selectedCaseId));
      if (this.dataSource?.paginator != undefined) {
        this.paginator = this.dataSource.paginator;
      }
      if (this.dataSource.sort != undefined) {
        this.sort = this.dataSource.sort;
      }
      this.loadingCases = false;
    });
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => {
      this.sortActive = this.sort.active;
      this.sortDirection = this.sort.direction;
      this.persistPreferences();
      this.reloadCasesWithNewParams();
    });

    this.sort.active = this.sortActive;
    this.sort.direction = this.sortDirection;
  }

  navigateToCaseDetail(caseOverviewHal: CaseOverviewHal) {
    this.loadingSpinnerService.show();
    const redirectRoute = new CaseOverviewHal(caseOverviewHal)._links.getLink('self')?.getRoute();
    this.router.navigate([redirectRoute]).then();
  }

  openDeleteCaseConfirmationModal(caseOverviewHal: CaseOverviewHal) {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'casesOverview.deleteCaseModal.title',
        message: this.translateService.instant('casesOverview.deleteCaseModal.message', {caseName: caseOverviewHal.name}),
        confirmButtonText: 'casesOverview.deleteCaseModal.confirm',
        cancelButtonText: 'casesOverview.deleteCaseModal.cancel'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteCase(caseOverviewHal);
      }
    });
  }

  deleteCase(caseOverviewHal: CaseOverviewHal) {
    this.loadingCases = true;
    const wasUnassigned = this.isUnassignedCase(caseOverviewHal);
    this.deleteCaseRequest(caseOverviewHal).subscribe({
      next: () => {
        this.selectedCases = this.selectedCases.filter(selectedCaseId => selectedCaseId !== caseOverviewHal.identification);
        this.updateCasesHal(this.paginator);
        if (wasUnassigned) {
          this.decrementUnassignedCaseCount();
        }
        this.snackBarService.open('casesOverview.snackbars.deleteSuccess', 3);
      },
      error: () => {
        this.loadingCases = false;
        this.snackBarService.open('casesOverview.snackbars.deleteError', 3, 'error');
      }
    });
  }

  openMoveCaseModal(caseOverviewHal: CaseOverviewHal) {
    const dialogRef = this.dialogService.open(MoveCaseComponent, {
      width: '40%',
      data: {
        currentTeamId: this.currentTeamId,
        caseName: caseOverviewHal.name
      }
    });
    dialogRef.afterClosed().subscribe(chosenTeam => {
      if (chosenTeam) {
        this.moveCase(caseOverviewHal, chosenTeam);
      }
    });
  }

  moveCase(caseOverviewHal: CaseOverviewHal, targetTeam: Team) {
    const moveLink = new CaseOverviewHal(caseOverviewHal)._links.getLink('move')?.href;
    if (!moveLink) {
      return;
    }
    this.loadingCases = true;
    const wasUnassigned = this.isUnassignedCase(caseOverviewHal);
    this.http.post(moveLink, {targetTeamId: targetTeam.identification}).subscribe({
      next: () => {
        this.selectedCases = this.selectedCases.filter(selectedCaseId => selectedCaseId !== caseOverviewHal.identification);
        this.updateCasesHal(this.paginator);
        // The case leaves this team's list, so its unassigned count drops just like on a delete.
        if (wasUnassigned) {
          this.decrementUnassignedCaseCount();
        }
        this.snackBarService.open('casesOverview.snackbars.moveSuccess', 3, undefined, {teamName: targetTeam.name});
      },
      error: () => {
        this.loadingCases = false;
        this.snackBarService.open('casesOverview.snackbars.moveError', 3, 'error');
      }
    });
  }

  selectionChanged(identification: string) {
    const caseToSelect = this.dataSource.data.find(caseOverview => caseOverview.identification === identification);
    if (!caseToSelect || !this.isCaseSelectable(caseToSelect)) {
      return;
    }
    if (this.selectedCases.includes(identification)) {
      this.selectedCases = this.selectedCases.filter(selectedCaseId => selectedCaseId !== identification);
      return;
    }
    this.selectedCases.push(identification);
  }

  selectionChangeAll() {
    const selectableCaseIds = this.getSelectableCases().map(caseOverview => caseOverview.identification);
    const allSelected = selectableCaseIds.length > 0 && selectableCaseIds.every(caseId => this.selectedCases.includes(caseId));
    this.selectedCases = allSelected ? [] : selectableCaseIds;
  }

  areAllCasesSelected(): boolean {
    const selectableCaseIds = this.getSelectableCases().map(caseOverview => caseOverview.identification);
    return selectableCaseIds.length > 0 && selectableCaseIds.every(caseId => this.selectedCases.includes(caseId));
  }

  areSomeCasesSelected(): boolean {
    const selectableCaseIds = this.getSelectableCases().map(caseOverview => caseOverview.identification);
    const selectedSelectableCaseCount = selectableCaseIds.filter(caseId => this.selectedCases.includes(caseId)).length;
    return selectedSelectableCaseCount > 0 && selectedSelectableCaseCount < selectableCaseIds.length;
  }

  isCaseSelectable(caseOverview: CaseOverviewHal): boolean {
    return !this.isCaseAutomated(caseOverview);
  }

  isCaseAutomated(caseOverview: CaseOverviewHal): boolean {
    return caseOverview.state?.toUpperCase() == 'AUTOMATED';
  }

  shouldShowUnassignedAssignee(caseOverview: CaseOverviewHal): boolean {
    return !caseOverview.assigneeName && (caseOverview._links?.hasLink('update') || this.isCaseAutomated(caseOverview));
  }

  getSelectableCaseCount(): number {
    return this.getSelectableCases().length;
  }

  canAllSelectedCasesBeDeleted(): boolean {
    return this.getSelectedCases().every(caseOverview => caseOverview?._links?.hasLink('delete'));
  }

  canAllSelectedCasesBeAssigned(): boolean {
    return this.getSelectedCases().every(caseOverview => caseOverview?._links?.hasLink('update'));
  }

  deleteSelectedCases() {
    const selectedCases = this.getSelectedCases();
    if (selectedCases.length === 0) {
      return;
    }

    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'casesOverview.deleteCasesModal.title',
        message: this.translateService.instant('casesOverview.deleteCasesModal.message', {caseCount: selectedCases.length}),
        confirmButtonText: 'casesOverview.deleteCasesModal.confirm',
        cancelButtonText: 'casesOverview.deleteCasesModal.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.loadingCases = true;
      const deleteRequests = selectedCases.map(caseOverview => {
        return this.deleteCaseRequest(caseOverview).pipe(
          map(() => true),
          catchError(() => of(false))
        );
      });

      forkJoin(deleteRequests).subscribe(deleteResults => {
        const unassignedDeletedCount = selectedCases.filter((c, i) => this.isUnassignedCase(c) && deleteResults[i]).length;
        this.selectedCases = [];
        this.updateCasesHal(this.paginator);
        if (unassignedDeletedCount > 0) {
          this.sessionService.incrementUnassignedCaseCount(-unassignedDeletedCount);
        }
        const allCasesDeleted = deleteResults.every(Boolean);
        this.snackBarService.open(allCasesDeleted ? 'casesOverview.snackbars.deleteSelectedSuccess' : 'casesOverview.snackbars.deleteSelectedError', 3, allCasesDeleted ? undefined : 'error');
      });
    });
  }

  assignSelectedCases() {
    const selectedCases = this.getSelectedCases();
    if (selectedCases.length === 0) {
      return;
    }

    const dialogRef = this.dialogService.open(AssignCaseComponent, {
      width: '40%',
      data: {
        teamId: this.currentTeamId,
        assignMultiple: selectedCases.length > 1,
        assignMultipleCount: selectedCases.length
      }
    });

    dialogRef.afterClosed().subscribe(assignee => {
      if (!assignee) {
        return;
      }

      this.loadingCases = true;
      const assignRequests = selectedCases.map(caseOverview => {
        return this.assignCaseRequest(caseOverview, assignee).pipe(
          map(() => true),
          catchError(() => of(false))
        );
      });

      forkJoin(assignRequests).subscribe(assignResults => {
        const willBeUnassigned = !assignee?.identification;
        const unassignedCount = selectedCases.filter((c, i) => this.isUnassignedCase(c) && !willBeUnassigned && assignResults[i]).length;
        const newlyUnassignedCount = selectedCases.filter((c, i) => !this.isUnassignedCase(c) && willBeUnassigned && assignResults[i]).length;
        this.selectedCases = [];
        this.updateCasesHal(this.paginator);
        if (unassignedCount > 0) {
          this.sessionService.incrementUnassignedCaseCount(-unassignedCount);
        }
        if (newlyUnassignedCount > 0) {
          this.sessionService.incrementUnassignedCaseCount(newlyUnassignedCount);
        }
        const allCasesAssigned = assignResults.every(Boolean);
        this.snackBarService.open(allCasesAssigned ? 'casesOverview.snackbars.assignSelectedSuccess' : 'casesOverview.snackbars.assignSelectedError', 3, allCasesAssigned ? undefined : 'error');
      });
    });
  }

  downloadSelectedCasesDocuments() {
    const selectedCases = this.getSelectedCases();
    if (!canAllSelectedCasesBeDownloaded(selectedCases)) {
      return;
    }

    this.loadingSpinnerService.show();
    downloadSelectedCasesDocumentsAsZip(this.http, selectedCases).pipe(
      tap((downloadResult: CaseZipDownloadResult) => {
        if (downloadResult.zipBlob) {
          downloadZipBlob(
            downloadResult.zipBlob,
            downloadResult.filename
          );
          return;
        }
        this.showFailedCaseDownloadMessage(downloadResult);
      }),
      finalize(() => this.loadingSpinnerService.hide())
    ).subscribe({
      error: () => {
        this.snackBarService.open('casesOverview.snackbars.downloadError', 3, 'error');
      }
    });
  }

  reloadCasesWithNewParams() {
    this.paginator.pageIndex = 0;
    this.updateCasesHal(this.paginator);
  }

  updateCasesHal(pageEvent: PageEvent): void {
    this.updateCases$.next(pageEvent);
  }

  private buildCasesUrl(pageEvent: PageEvent): URL {
    return buildCasesUrl({
      baseUrl: this.pagedCaseOverviewHal._links.getLink('self')!.href,
      page: pageEvent.pageIndex,
      size: pageEvent.pageSize,
      sortActive: this.sortActive,
      sortDirection: this.sortDirection,
      searchTerm: this.searchCasesControl.value,
      userFilter: this.onlyShowMyCases ? this.sessionService.getSession()?.user?.identification ?? null : null,
      stateFilters: this.selectedStateFilters,
      availableStates: this.stateFilterOptions
    });
  }

  openAssignCaseModal(casee: CaseOverviewHal) {
    const dialogRef = this.dialogService.open(AssignCaseComponent, {
      width: '40%',
      data: {teamId: this.currentTeamId}
    });
    dialogRef.afterClosed().subscribe(assignee => {
      if (assignee !== undefined && assignee?.identification?.length > 0) {
        this.assignCase(casee, assignee);
      }
    });
  }

  assignCase(casee: CaseOverviewHal, user: UserHal | null) {
    const wasUnassigned = this.isUnassignedCase(casee);
    const willBeUnassigned = !user?.identification;
    casee = new CaseOverviewHal(casee);
    let caseDto = new CaseDtoModel(casee);
    caseDto.assigneeUserId = user?.identification ?? '';
    this.assignCaseRequest(casee, user).subscribe({
      next: () => {
        this.updateCasesHal(this.paginator);
        if (wasUnassigned && !willBeUnassigned) {
          this.decrementUnassignedCaseCount();
        } else if (!wasUnassigned && willBeUnassigned) {
          this.incrementUnassignedCaseCount();
        }
        this.snackBarService.open('casesOverview.snackbars.assignSuccess', 3);
      },
      error: () => {
        this.snackBarService.open('casesOverview.snackbars.assignError', 3, 'error');
      }
    });
  }

  protected createCase() {
    this.loadingSpinnerService.show();
    this.router.navigate([this.sessionService.getSession().currentTeam?._links.getLink('createCase')?.getRoute()]).then();
  }

  private assignCaseRequest(casee: CaseOverviewHal, user: UserHal | null) {
    const caseOverview = new CaseOverviewHal(casee);
    const caseDto = new CaseDtoModel(caseOverview);
    caseDto.assigneeUserId = user ? user.identification : '';
    return this.http.put<CaseOverviewHal>(caseOverview._links.getLink('self')!.href, caseDto);
  }

  protected getSelectedCases(): CaseOverviewHal[] {
    const selectedCaseIds = new Set(this.selectedCases);
    return this.dataSource.data.filter(caseOverview => selectedCaseIds.has(caseOverview.identification));
  }

  private getSelectableCases(): CaseOverviewHal[] {
    return this.dataSource.data.filter(caseOverview => this.isCaseSelectable(caseOverview));
  }

  private deleteCaseRequest(caseOverviewHal: CaseOverviewHal) {
    return this.http.delete(new CaseOverviewHal(caseOverviewHal)._links.getLink('delete')!.href);
  }

  private decrementUnassignedCaseCount() {
    this.sessionService.incrementUnassignedCaseCount(-1);
  }

  private incrementUnassignedCaseCount() {
    this.sessionService.incrementUnassignedCaseCount(1);
  }

  protected isUnassignedCase(caseOverview: CaseOverviewHal): boolean {
    return caseOverview.state?.toUpperCase() === 'UNASSIGNED';
  }

  toggleOnlyShowMyCases() {
    this.onlyShowMyCases = !this.onlyShowMyCases;
    this.persistPreferences();
    this.reloadCasesWithNewParams();
  }

  updateStateFilters(selectedStates: string[]) {
    this.selectedStateFilters = selectedStates;
    this.persistPreferences();
    this.reloadCasesWithNewParams();
  }

  clearSearch(input?: HTMLInputElement) {
    this.searchCasesControl.setValue('');
    setTimeout(() => input?.focus(), 0);
  }

  private showFailedCaseDownloadMessage(downloadResult: CaseZipDownloadResult) {
    const translationKey = getCaseDownloadFailureSnackbarKey(downloadResult.failureReason);
    const caseName = downloadResult.caseName || downloadResult.caseId;
    const message = this.translateService.instant(translationKey, {caseName});
    this.snackBarService.open(message, 5, 'error');
  }

  private applyStoredPreferences() {
    const stored = loadPreferences();
    if (!stored) {
      return;
    }

    if (stored.searchTerm !== undefined) {
      this.searchCasesControl.setValue(stored.searchTerm, {emitEvent: false});
    }

    if (stored.onlyShowMyCases !== undefined) {
      this.onlyShowMyCases = stored.onlyShowMyCases;
    }

    if (stored.stateFilters) {
      const filteredStates = stored.stateFilters.filter(state => this.stateFilterOptions.includes(state));
      this.selectedStateFilters = filteredStates.length > 0 ? filteredStates : [];
    }

    if (stored.sortActive) {
      this.sortActive = stored.sortActive;
    }
    if (stored.sortDirection) {
      this.sortDirection = stored.sortDirection;
    }
  }

  private persistPreferences() {
    try {
      const preferences: CasesOverviewPreferences = {
        searchTerm: this.searchCasesControl.value ?? '',
        onlyShowMyCases: this.onlyShowMyCases,
        sortActive: this.sortActive ?? null,
        sortDirection: this.sortDirection ?? null,
        stateFilters: this.selectedStateFilters
      };
      localStorage.setItem(CASES_OVERVIEW_STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to store cases overview preferences to localStorage.', error);
    }
  }

  protected readonly canAllSelectedCasesBeDownloaded = canAllSelectedCasesBeDownloaded;
}
